import { ChangeEvent, StateSetter, useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { Column, Row, IconButton, Button, LabeledInput } from 'components/Blocks';
import { PlusIcon, XIcon } from 'components/Icons';

import { getID } from 'utils/id';
import { generateRandomHexColor } from 'utils/color';

import { FormTeamService } from './types';

interface ServiceTagsProps {
  children?: never;
  service: FormTeamService | null;
  setService: StateSetter<FormTeamService | null>;
}

function ServiceTags({ service, setService }: ServiceTagsProps): JSX.Element {
  const { t } = useLang();

  const addTag = useCallback(() => {
    setService((oldData) => {
      if (!oldData) return oldData;

      const { tags, ...old } = oldData;

      return {
        ...old,
        tags: [...tags, { id: getID(), name: '', color: generateRandomHexColor() }],
      };
    });
  }, [setService]);

  const removeTag = useCallback(
    (id: string) => {
      setService((oldData) => {
        if (!oldData) return oldData;

        const { tags, ...old } = oldData;

        return {
          ...old,
          tags: tags.filter((t) => t.id !== id),
        };
      });
    },
    [setService],
  );

  const updateTag = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { value, name } = target;
      const [property, index] = name.split('-');

      setService((old) => {
        const { tags, ...rest } = old as FormTeamService;

        return {
          ...rest,
          tags: tags.map((tag, tagIndex) =>
            Number(index) === tagIndex ? { ...tag, [property]: value } : tag,
          ),
        };
      });
    },
    [setService],
  );

  return (
    <Column gap="6">
      <Row>
        <p>{t('tags')}</p>

        <Button format="square" round onClick={addTag}>
          <PlusIcon size={16} />
        </Button>
      </Row>

      {service?.tags.map(({ name, color, id }, index) => (
        <Row key={`tag-${index}`} className="pl-2 phone:justify-between">
          <span>{index + 1}.</span>

          <LabeledInput
            required
            size="small"
            name={`name-${index}`}
            value={name}
            onChange={updateTag}
          />

          <input
            className="min-w-6 shrink-0 self-end rounded-full bg-transparent"
            name={`color-${index}`}
            type="color"
            value={color}
            onChange={updateTag}
          />

          {!service?.createdAt && (
            <IconButton className="self-end" onClick={() => removeTag(id)}>
              <XIcon />
            </IconButton>
          )}
        </Row>
      ))}
    </Column>
  );
}

export default ServiceTags;
